/**
 * Task Auto Creation Service
 * 
 * Сервис для автоматического создания задач при создании заказов и аренд
 * Соблюдает STYLEGUIDE.md:
 * - camelCase именование
 * - Корректная типизация
 * - Обработка ошибок
 */

import { Task } from '@/types/entities/index';
import { useTasksUnified } from '@/domains/tasks/hooks/useTasksUnified';
import { useAuth } from '@/context/AuthContext';

export interface TaskAutoCreationSettings {
  autoCreateTasksForOrders: boolean;
  autoCreateTasksForRentals: boolean;
  defaultTaskCategory: string;
  defaultTaskStatus: string;
}

export interface OrderTaskData {
  orderId: string;
  clientId: string;
  clientName: string;
  orderType: 'purchase' | 'rental' | 'mixed';
  totalAmount: number;
  items: Array<{
    name: string;
    quantity: number;
    type: 'inventory' | 'store' | 'service';
  }>;
}

export interface RentalTaskData {
  rentalId: string;
  clientId: string;
  clientName: string;
  inventoryItems: Array<{
    name: string;
    quantity: number;
  }>;
  startDate: Date;
  endDate: Date;
}

/**
 * Создает задачи для заказа согласно настройкам
 */
export const createTasksForOrder = async (
  orderData: OrderTaskData,
  settings: TaskAutoCreationSettings
): Promise<Task[]> => {
  if (!settings.autoCreateTasksForOrders) {
    return [];
  }

  const tasks: Partial<Task>[] = [];

  // Основная задача по обработке заказа
  tasks.push({
    title: `Обработать заказ для ${orderData.clientName}`,
    description: `Заказ на сумму ${orderData.totalAmount} руб. Товаров: ${orderData.items.length}`,
    type: 'task',
    category: settings.defaultTaskCategory,
    status: settings.defaultTaskStatus,
    relatedOrderId: orderData.orderId,
    relatedClientId: orderData.clientId,
    notification: true,
    completed: false,
  });

  // Задачи для аренды (если есть арендуемые товары)
  if (orderData.orderType === 'rental' || orderData.orderType === 'mixed') {
    const rentalItems = orderData.items.filter(item => item.type === 'inventory');
    
    if (rentalItems.length > 0) {
      tasks.push({
        title: `Подготовить оборудование для аренды`,
        description: `Подготовить к выдаче: ${rentalItems.map(item => `${item.name} (${item.quantity} шт.)`).join(', ')}`,
        type: 'task',
        category: 'rental',
        status: settings.defaultTaskStatus,
        relatedOrderId: orderData.orderId,
        relatedClientId: orderData.clientId,
        notification: true,
        completed: false,
      });

      tasks.push({
        title: `Проверить возврат оборудования`,
        description: `Проверить состояние возвращенного оборудования от ${orderData.clientName}`,
        type: 'task',
        category: 'rental',
        status: 'pending',
        relatedOrderId: orderData.orderId,
        relatedClientId: orderData.clientId,
        notification: true,
        completed: false,
      });
    }
  }

  // Задачи для покупки товаров (если есть товары магазина)
  if (orderData.orderType === 'purchase' || orderData.orderType === 'mixed') {
    const storeItems = orderData.items.filter(item => item.type === 'store');
    
    if (storeItems.length > 0) {
      tasks.push({
        title: `Собрать товары для выдачи`,
        description: `Собрать товары: ${storeItems.map(item => `${item.name} (${item.quantity} шт.)`).join(', ')}`,
        type: 'task',
        category: 'order',
        status: settings.defaultTaskStatus,
        relatedOrderId: orderData.orderId,
        relatedClientId: orderData.clientId,
        notification: true,
        completed: false,
      });
    }
  }

  // Задачи для услуг
  const serviceItems = orderData.items.filter(item => item.type === 'service');
  if (serviceItems.length > 0) {
    tasks.push({
      title: `Выполнить услуги для клиента`,
      description: `Услуги: ${serviceItems.map(item => `${item.name} (${item.quantity} шт.)`).join(', ')}`,
      type: 'task',
      category: 'service',
      status: settings.defaultTaskStatus,
      relatedOrderId: orderData.orderId,
      relatedClientId: orderData.clientId,
      notification: true,
      completed: false,
    });
  }

  // Создание задач через API
  const createdTasks: Task[] = [];
  
  try {
    // Здесь должен быть вызов API для создания задач
    // Пока используем заглушку
    for (const taskData of tasks) {
      console.log('Creating task:', taskData.title);
      // const createdTask = await createTask(taskData);
      // createdTasks.push(createdTask);
    }
  } catch (error) {
    console.error('Error creating tasks for order:', error);
    throw new Error(`Не удалось создать задачи для заказа: ${error}`);
  }

  return createdTasks;
};

/**
 * Создает задачи для аренды согласно настройкам
 */
export const createTasksForRental = async (
  rentalData: RentalTaskData,
  settings: TaskAutoCreationSettings
): Promise<Task[]> => {
  if (!settings.autoCreateTasksForRentals) {
    return [];
  }

  const tasks: Partial<Task>[] = [];

  // Задача подготовки оборудования
  tasks.push({
    title: `Подготовить оборудование для аренды`,
    description: `Подготовить к выдаче: ${rentalData.inventoryItems.map(item => `${item.name} (${item.quantity} шт.)`).join(', ')}`,
    type: 'task',
    category: 'rental',
    status: settings.defaultTaskStatus,
    relatedClientId: rentalData.clientId,
    deadline: rentalData.startDate.toISOString(),
    notification: true,
    completed: false,
  });

  // Задача проверки возврата
  const returnDeadline = new Date(rentalData.endDate);
  returnDeadline.setHours(returnDeadline.getHours() + 2); // Через 2 часа после окончания аренды

  tasks.push({
    title: `Проверить возврат оборудования`,
    description: `Проверить состояние возвращенного оборудования от ${rentalData.clientName}`,
    type: 'task',
    category: 'rental',
    status: 'pending',
    relatedClientId: rentalData.clientId,
    deadline: returnDeadline.toISOString(),
    notification: true,
    completed: false,
  });

  // Создание задач через API
  const createdTasks: Task[] = [];
  
  try {
    // Здесь должен быть вызов API для создания задач
    // Пока используем заглушку
    for (const taskData of tasks) {
      console.log('Creating rental task:', taskData.title);
      // const createdTask = await createTask(taskData);
      // createdTasks.push(createdTask);
    }
  } catch (error) {
    console.error('Error creating tasks for rental:', error);
    throw new Error(`Не удалось создать задачи для аренды: ${error}`);
  }

  return createdTasks;
};

/**
 * Хук для автоматического создания задач
 */
export const useTaskAutoCreation = () => {
  const { user } = useAuth();
  const { createTask } = useTasksUnified();

  const createTasksForOrderHook = async (
    orderData: OrderTaskData,
    settings: TaskAutoCreationSettings
  ): Promise<Task[]> => {
    if (!user || !settings.autoCreateTasksForOrders) {
      return [];
    }

    const tasks: Omit<Task, 'id' | 'userId' | 'createdAt' | 'updatedAt'>[] = [];

    // Основная задача по обработке заказа
    tasks.push({
      title: `Обработать заказ для ${orderData.clientName}`,
      description: `Заказ на сумму ${orderData.totalAmount} руб. Товаров: ${orderData.items.length}`,
      type: 'task',
      category: settings.defaultTaskCategory,
      status: settings.defaultTaskStatus,
      relatedOrderId: orderData.orderId,
      relatedClientId: orderData.clientId,
      notification: true,
      completed: false,
    });

    // Создание задач через хук
    const createdTasks: Task[] = [];
    
    try {
      for (const taskData of tasks) {
        const createdTask = await createTask(taskData);
        createdTasks.push(createdTask);
      }
    } catch (error) {
      console.error('Error creating tasks for order:', error);
      throw new Error(`Не удалось создать задачи для заказа: ${error}`);
    }

    return createdTasks;
  };

  return {
    createTasksForOrder: createTasksForOrderHook,
    /**
     * Создает задачи для аренды через hook API
     */
    createTasksForRental: async (
      rentalData: RentalTaskData,
      settings: TaskAutoCreationSettings
    ): Promise<Task[]> => {
      if (!user || !settings.autoCreateTasksForRentals) {
        return [];
      }

      const tasks: Omit<Task, 'id' | 'userId' | 'createdAt' | 'updatedAt'>[] = [];

      // Задача подготовки оборудования
      tasks.push({
        title: `Подготовить оборудование для аренды`,
        description: `Подготовить к выдаче: ${rentalData.inventoryItems.map(i => `${i.name} (${i.quantity} шт.)`).join(', ')}`,
        type: 'task',
        category: 'rental',
        status: settings.defaultTaskStatus,
        relatedClientId: rentalData.clientId,
        deadline: rentalData.startDate.toISOString(),
        notification: true,
        completed: false,
      });

      // Задача проверки возврата (через 2 часа после конца аренды)
      const returnDeadline = new Date(rentalData.endDate);
      returnDeadline.setHours(returnDeadline.getHours() + 2);

      tasks.push({
        title: `Проверить возврат оборудования`,
        description: `Проверить состояние возвращенного оборудования от ${rentalData.clientName}`,
        type: 'task',
        category: 'rental',
        status: 'pending',
        relatedClientId: rentalData.clientId,
        deadline: returnDeadline.toISOString(),
        notification: true,
        completed: false,
      });

      const createdTasks: Task[] = [];
      try {
        for (const t of tasks) {
          const created = await createTask(t);
          createdTasks.push(created);
        }
      } catch (error) {
        console.error('Error creating tasks for rental:', error);
        throw new Error(`Не удалось создать задачи для аренды: ${error}`);
      }

      return createdTasks;
    },
  };
};
